import {
  SENSITIVE_VAULT_SALT_BYTES,
  SENSITIVE_VAULT_IV_BYTES,
  SENSITIVE_VAULT_KEY_BYTES,
  SENSITIVE_VAULT_KDF,
  SENSITIVE_VAULT_PBKDF2_ITERATIONS,
  SENSITIVE_VAULT_VERSION,
  SENSITIVE_VAULT_AES_ALGO,
} from './vaultCryptoConfig';

export function normalizeVaultKdf(kdf) {
  const value = String(kdf || '').toLowerCase();
  if (value === 'pbkdf2' || value === 'pbkdf2-sha256') return 'pbkdf2';
  if (value === 'argon2id') return 'argon2id';
  return value;
}

export function normalizeVaultPassphrase(passphrase) {
  return String(passphrase || '').normalize('NFKC');
}

export function ensureUint8Array(value) {
  if (value instanceof Uint8Array) {
    return value;
  }

  if (value instanceof ArrayBuffer) {
    return new Uint8Array(value);
  }

  if (ArrayBuffer.isView(value)) {
    return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  }

  throw new Error('Expected Uint8Array-compatible value');
}

export function bytesToBase64(bytes) {
  const arr = ensureUint8Array(bytes);

  if (typeof Buffer !== 'undefined') {
    return Buffer.from(arr).toString('base64');
  }

  let binary = '';
  for (let i = 0; i < arr.length; i += 1) {
    binary += String.fromCharCode(arr[i]);
  }
  return btoa(binary);
}

export function base64ToBytes(base64) {
  const clean = String(base64 || '').trim();

  if (!clean) {
    return new Uint8Array();
  }

  if (typeof Buffer !== 'undefined') {
    return new Uint8Array(Buffer.from(clean, 'base64'));
  }

  const binary = atob(clean);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    out[i] = binary.charCodeAt(i);
  }
  return out;
}

export function utf8ToBytes(text) {
  return new TextEncoder().encode(String(text || ''));
}

export function bytesToUtf8(bytes) {
  return new TextDecoder().decode(ensureUint8Array(bytes));
}

export function concatBytes(...parts) {
  const normalized = parts.map(ensureUint8Array);
  const totalLength = normalized.reduce((sum, part) => sum + part.length, 0);
  const out = new Uint8Array(totalLength);

  let offset = 0;
  for (const part of normalized) {
    out.set(part, offset);
    offset += part.length;
  }

  return out;
}

export function getVaultCryptoMetadata() {
  return {
    version: SENSITIVE_VAULT_VERSION,
    kdf: SENSITIVE_VAULT_KDF,
    iterations: SENSITIVE_VAULT_PBKDF2_ITERATIONS,
    keyBytes: SENSITIVE_VAULT_KEY_BYTES,
    saltBytes: SENSITIVE_VAULT_SALT_BYTES,
    ivBytes: SENSITIVE_VAULT_IV_BYTES,
    contentCipher: SENSITIVE_VAULT_AES_ALGO,
  };
}

export function assertSupportedVaultMetadata(metadata) {
  if (!metadata) {
    throw new Error('Vault metadata is required');
  }

  if (metadata.version !== SENSITIVE_VAULT_VERSION) {
    throw new Error(`Unsupported vault version: ${metadata.version}`);
  }

  if (metadata.kdf !== SENSITIVE_VAULT_KDF) {
    throw new Error(`Unsupported vault KDF: ${metadata.kdf}`);
  }

  if (Number(metadata.iterations) !== SENSITIVE_VAULT_PBKDF2_ITERATIONS) {
    throw new Error(
      `Unsupported PBKDF2 iteration count: ${metadata.iterations}`
    );
  }
}

/**
 * Platform-specific random bytes provider must be registered at app startup.
 * Web and mobile can each provide their own implementation.
 */
let randomBytesImpl = null;

export function setVaultRandomBytesImpl(fn) {
  if (typeof fn !== 'function') {
    throw new Error('setVaultRandomBytesImpl requires a function');
  }
  randomBytesImpl = fn;
}

export function getRandomBytes(length) {
  if (!Number.isInteger(length) || length <= 0) {
    throw new Error('getRandomBytes length must be a positive integer');
  }

  if (!randomBytesImpl) {
    throw new Error(
      'Vault random bytes implementation has not been registered'
    );
  }

  const value = randomBytesImpl(length);
  const bytes = ensureUint8Array(value);

  if (bytes.length !== length) {
    throw new Error(
      `Random bytes implementation returned ${bytes.length} bytes, expected ${length}`
    );
  }

  return bytes;
}

export function generateVaultSalt() {
  return getRandomBytes(SENSITIVE_VAULT_SALT_BYTES);
}

export function generateVaultIv() {
  return getRandomBytes(SENSITIVE_VAULT_IV_BYTES);
}
